import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { seoConfig, defaultSEO, schemaTemplates } from '../config/seoConfig';

const AppSEO = ({ overrides = {} }) => {
  const location = useLocation();
  
  // Get SEO config for current route, fallback to default
  const routeConfig = seoConfig[location.pathname] || defaultSEO;
  
  // Merge route config with any component-specific overrides
  const config = { ...routeConfig, ...overrides };
  
  // Build full title with brand name
  const baseTitle = "BagpipeBlasphemy";
  const fullTitle = config.title && config.title !== baseTitle 
    ? `${config.title} | ${baseTitle}` 
    : baseTitle;

  // Generate structured data based on route
  const getStructuredData = () => {
    const pathname = location.pathname;
    
    // Home page gets special WebApplication schema
    if (pathname === '/') {
      return schemaTemplates.home;
    }
    
    // Instruction page gets Course schema
    if (pathname === '/instruction') {
      return {
        ...schemaTemplates.instruction,
        "name": config.title,
        "description": config.description
      };
    }
    
    // Games get Game schema
    if (pathname === '/blaster' || pathname === '/note-battle' || pathname === '/battle') {
      return {
        ...schemaTemplates.game,
        "name": config.title,
        "description": config.description,
        "url": `https://bagpipeblasphemy.com${pathname === '/battle' ? '/note-battle' : pathname}`
      };
    }
    
    // Tools get SoftwareApplication schema
    if (['/tuner', '/metronome', '/chantersync', '/chanter-test'].includes(pathname)) {
      return {
        ...schemaTemplates.tool,
        "name": config.title,
        "description": config.description,
        "url": `https://bagpipeblasphemy.com${pathname}`
      };
    }
    
    // Band discovery gets directory schema
    if (pathname === '/bands') {
      return schemaTemplates.bands;
    }
    
    // Band registration gets registration schema
    if (pathname === '/band-registration') {
      return schemaTemplates.bandRegistration;
    }
    
    // Return null for other pages (no structured data)
    return null;
  };

  const structuredData = getStructuredData();
  const canonicalPath = config.canonical || location.pathname;
  const canonicalUrl = `https://bagpipeblasphemy.com${canonicalPath}`;
  const ogImageUrl = config.ogImage ? `https://bagpipeblasphemy.com${config.ogImage}` : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={config.description} />
      <meta name="keywords" content={config.keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={config.description} />
      <meta property="og:site_name" content="BagpipeBlasphemy" />
      {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}
      {ogImageUrl && <meta property="og:image:width" content="1200" />}
      {ogImageUrl && <meta property="og:image:height" content="630" />}
      {ogImageUrl && <meta property="og:image:alt" content={config.title} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={config.description} />
      {ogImageUrl && <meta name="twitter:image" content={ogImageUrl} />}
      {ogImageUrl && <meta name="twitter:image:alt" content={config.title} />}
      
      {/*
        SPA routes still render the empty app.html shell to crawlers (#267/#268).
        Keep noindex until #269 static landings replace money URLs. Static /,
        /learn/*, /faq/* do not use this component.
      */}
      <meta name="robots" content="noindex, follow" />
      <meta name="language" content="English" />
      <meta name="author" content="BagpipeBlasphemy" />
      <meta name="theme-color" content="#1a1a1a" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Additional meta tags for specific routes */}
      {location.pathname === '/' && (
        <>
          <meta name="application-name" content="BagpipeBlasphemy" />
          <meta name="category" content="Education, Music, Games" />
        </>
      )}
      
      {(location.pathname === '/blaster' ||
        location.pathname === '/note-battle' ||
        location.pathname === '/battle') && (
        <meta name="category" content="Games, Music, Education" />
      )}
      
      {location.pathname === '/tuner' && (
        <>
          <meta name="category" content="Music, Tools, Utilities" />
          <meta name="keywords" content={`${config.keywords}, online tuner, instrument tuner, pitch detection`} />
        </>
      )}
    </Helmet>
  );
};

export default AppSEO; 